'use client';

import { useEffect, useState } from 'react';
import { Plus, Trash2, RotateCcw, Download, FileText } from 'lucide-react';

type Category = 'must' | 'should' | 'could' | 'wont';

type Item = {
  id: string;
  name: string;
  notes: string;
  category: Category;
};

const COLUMNS: { value: Category; label: string; sub: string; pillClass: string; bgClass: string }[] = [
  {
    value: 'must',
    label: 'Must Have',
    sub: 'Critical — release fails without it',
    pillClass: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
    bgClass: 'bg-red-50/40 dark:bg-red-500/5',
  },
  {
    value: 'should',
    label: 'Should Have',
    sub: 'Important but not blocking',
    pillClass: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
    bgClass: 'bg-amber-50/40 dark:bg-amber-500/5',
  },
  {
    value: 'could',
    label: 'Could Have',
    sub: 'Nice if capacity allows',
    pillClass: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
    bgClass: 'bg-sky-50/40 dark:bg-sky-500/5',
  },
  {
    value: 'wont',
    label: "Won't Have",
    sub: 'Explicitly out of scope',
    pillClass: 'bg-muted text-muted-foreground',
    bgClass: 'bg-muted/30',
  },
];

const STORAGE_KEY = 'planetroadmap.moscow-tool.v1';

function newItem(category: Category = 'could', name = ''): Item {
  return {
    id: Math.random().toString(36).slice(2),
    name,
    notes: '',
    category,
  };
}

export function MoscowTool() {
  const [items, setItems] = useState<Item[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Item[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          setItems(parsed);
          setHydrated(true);
          return;
        }
      }
    } catch {}
    setItems([
      { ...newItem('must', 'Login with email and password') },
      { ...newItem('must', 'Mobile responsive layout') },
      { ...newItem('should', 'SSO with Google') },
      { ...newItem('could', 'Dark mode') },
      { ...newItem('could', 'Custom themes') },
      { ...newItem('wont', 'On-prem deployment (this release)') },
    ]);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      console.warn('Failed to persist to localStorage:', err);
    }
  }, [items, hydrated]);

  function update(id: string, patch: Partial<Item>) {
    setItems((curr) => curr.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }

  function remove(id: string) {
    setItems((curr) => curr.filter((i) => i.id !== id));
  }

  function addToCategory(category: Category) {
    setItems((curr) => [...curr, newItem(category)]);
  }

  function reset() {
    if (!confirm('Clear all items and start over?')) return;
    setItems([]);
  }

  function exportCsv() {
    const labelFor = (c: Category) => COLUMNS.find((col) => col.value === c)!.label;
    const rows = [
      ['Name', 'Category', 'Notes'],
      ...items.map((i) => [i.name || 'Untitled', labelFor(i.category), i.notes]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    download(csv, 'moscow.csv', 'text/csv');
  }

  function exportMarkdown() {
    const lines: string[] = ['# MoSCoW Prioritization', ''];
    for (const col of COLUMNS) {
      const colItems = items.filter((i) => i.category === col.value);
      if (colItems.length === 0) continue;
      lines.push(`## ${col.label}`, '');
      for (const item of colItems) {
        const name = item.name || 'Untitled';
        if (item.notes.trim()) {
          lines.push(`- **${name}** — ${item.notes.trim()}`);
        } else {
          lines.push(`- ${name}`);
        }
      }
      lines.push('');
    }
    download(lines.join('\n'), 'moscow.md', 'text/markdown');
  }

  function download(content: string, filename: string, type: string) {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!hydrated) {
    return (
      <div className="bg-white dark:bg-card border border-border/60 rounded-2xl p-12 text-center text-muted-foreground">
        Loading tool…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {COLUMNS.map((col) => {
          const colItems = items.filter((i) => i.category === col.value);
          return (
            <div
              key={col.value}
              className={`rounded-2xl border border-border/60 ${col.bgClass} flex flex-col`}
            >
              <div className="p-4 border-b border-border/60">
                <div
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${col.pillClass}`}
                >
                  {col.label}
                </div>
                <p className="text-xs text-muted-foreground mt-2">{col.sub}</p>
                <p className="text-[11px] text-muted-foreground/70 mt-1">
                  {colItems.length} {colItems.length === 1 ? 'item' : 'items'}
                </p>
              </div>
              <div className="p-3 space-y-2 flex-1 min-h-[120px]">
                {colItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white dark:bg-card border border-border/60 rounded-xl p-3 group"
                  >
                    <div className="flex items-start gap-2">
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => update(item.id, { name: e.target.value })}
                        placeholder="Untitled item"
                        className="flex-1 bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-1 py-0.5 text-sm font-medium"
                      />
                      <button
                        onClick={() => remove(item.id)}
                        aria-label="Remove item"
                        className="text-muted-foreground/60 hover:text-red-500 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <textarea
                      value={item.notes}
                      onChange={(e) => update(item.id, { notes: e.target.value })}
                      placeholder="Notes (optional)"
                      rows={item.notes ? 2 : 1}
                      className="w-full mt-1 bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary rounded px-1 py-0.5 text-xs text-muted-foreground resize-none"
                    />
                    <select
                      value={item.category}
                      onChange={(e) => update(item.id, { category: e.target.value as Category })}
                      className="mt-2 w-full text-xs bg-transparent border border-border/60 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      {COLUMNS.map((c) => (
                        <option key={c.value} value={c.value}>
                          Move to {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
                <button
                  onClick={() => addToCategory(col.value)}
                  className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-border/60 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-white/60 dark:hover:bg-card transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add item
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3 justify-end items-center pt-2">
        <button
          onClick={exportCsv}
          disabled={items.length === 0}
          className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50 transition-colors"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
        <button
          onClick={exportMarkdown}
          disabled={items.length === 0}
          className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 disabled:opacity-50 transition-colors"
        >
          <FileText className="w-4 h-4" />
          Export Markdown
        </button>
        <button
          onClick={reset}
          className="inline-flex items-center gap-2 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Reset
        </button>
      </div>

      <p className="text-xs text-muted-foreground text-center pt-2">
        Your board is saved automatically in your browser. Nothing leaves this page.
      </p>
    </div>
  );
}
